import React, { useState } from 'react';
import { Loader2, AlertCircle, Trash2 } from 'lucide-react';
import DocumentCard from './DocumentCard';

// Modal de confirmation de suppression - Identique à ObjectifsList.js
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, documentName, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Supprimer le document</h3>
            <p className="text-sm text-gray-500">Cette action est irréversible</p>
          </div>
        </div>
        
        <p className="text-gray-700 mb-6">
          Êtes-vous sûr de vouloir supprimer le document "{documentName}" ? 
          Toutes les données associées (concepts, passages, etc.) seront définitivement perdues.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Supprimer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const DocumentList = ({ 
  documents = [], 
  loading = false, 
  onDelete, 
  onViewDetails, 
  onSelectConcept,
  onPassageClick,
  onLoadMore, 
  hasMore = false,
  deletingDocIds = new Set()
}) => {
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, document: null });
  const [isDeleting, setIsDeleting] = useState(false);

  if (!documents.length && !loading) return null;

  // Tri automatique par date (plus récents en premier)
  const sortedDocuments = [...documents].sort((a, b) => {
    const dateA = new Date(a.metadata?.uploadedAt || 0);
    const dateB = new Date(b.metadata?.uploadedAt || 0);
    return dateB - dateA; // Décroissant (plus récent en premier)
  });

  // Gérer la suppression avec confirmation
  const handleDeleteClick = (documentId) => {
    const document = documents.find(doc => doc.id === documentId);
    if (document) {
      setDeleteModal({ isOpen: true, document });
    }
  };

  // Confirmer la suppression
  const handleConfirmDelete = async () => {
    if (!deleteModal.document) return;
    
    const documentId = deleteModal.document.id;
    setIsDeleting(true);
    
    try {
      // Appeler la fonction onDelete fournie par le parent
      if (onDelete) {
        await onDelete(documentId);
      }
      
      setDeleteModal({ isOpen: false, document: null });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Annuler la suppression
  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, document: null });
  };

  return (
    <>
      {/* Liste des documents - Espacement minimal */}
      <div className="space-y-3">
        {sortedDocuments.map((doc) => (
          <DocumentCard
            key={doc.id || `doc-${doc.metadata?.title}-${doc.metadata?.uploadedAt}`}
            document={doc}
            onDelete={handleDeleteClick} // Utiliser notre gestionnaire avec confirmation
            onViewDetails={onViewDetails}
            onSelectConcept={onSelectConcept}
            onPassageClick={onPassageClick}
            isDeleting={deletingDocIds.has(doc.id)}
          />
        ))}
      </div>
      
      {/* État de chargement */}
      {loading && (
        <div className="text-center py-8 mt-4 bg-white rounded-lg border border-gray-200">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-3" />
          <p className="text-sm text-gray-600 font-medium">Chargement des résultats...</p>
        </div>
      )}
      
      {/* Bouton Charger plus */}
      {hasMore && !loading && (
        <div className="text-center py-6 mt-4">
          <button
            onClick={onLoadMore}
            className="px-6 py-3 bg-white border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-blue-400 hover:text-blue-700 transition-all"
          >
            Charger plus de résultats
          </button>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        documentName={deleteModal.document?.title || deleteModal.document?.metadata?.title || 'Document sans titre'}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default DocumentList;
